import { parse } from 'toolkit/utils/date-format-parse/parse.js'
import { getWeek } from 'toolkit/utils/date-format-parse/util.js'
import { isValidRangeDate, isValidDate, isValidDates, format, pick, isObject, mergeDeep } from 'toolkit/utils/date'
import { getLocale } from './locale'
import DatePopup from './datePopup'

import sCalendarPanel from './calendar/calendar-panel'
import sCalendarRange from './calendar/calendar-range'
import sTimePanel from './time/time-panel'
import sDatetimePanel from './datetime/datetime-panel'
import sTimeRange from './time/time-range'
import sDatetimeRange from './datetime/datetime-range'

const componentMap = {
  default: sCalendarPanel,
  time: sTimePanel,
  datetime: sDatetimePanel,
}
const componentRangeMap = {
  default: sCalendarRange,
  time: sTimeRange,
  datetime: sDatetimeRange,
}

export default {
  name: 'sDatePicker',
  provide() {
    return {
      // make locale reactive
      getLocale: () => this.locale,
      getWeek: this.getWeek,
      prefixClass: this.prefixClass,
      hideDoubleArrows: this.hideDoubleArrows,
      dispatchDatePicker: this.$emit.bind(this),
      disabled: this.disabled,
    }
  },
  props: {
    ...sDatetimePanel.props,
    value: {},
    valueType: {
      type: String,
      default: 'date', // date, format, timestamp, or token like 'YYYY-MM-DD'
    },
    type: {
      type: String, // ['date', 'datetime', 'time', 'year', 'month', 'week']
      default: 'date',
    },
    format: {
      type: String,
    },
    formatter: {
      type: Object,
    },
    range: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    rangeSeparator: {
      type: String,
    },
    placeholder: {
      type: String,
      default: '',
    },
    editable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    prefixClass: {
      type: String,
      default: 's-datepicker',
    },
    appendToBody: {
      type: Boolean,
      default: true,
    },
    open: {
      type: Boolean,
      default: undefined,
    },
    popupClass: {},
    popupStyle: {
      type: Object,
      default: () => ({}),
    },
    inline: {
      type: Boolean,
      default: false,
    },
    confirm: {
      type: Boolean,
      default: false,
    },
    confirmText: {
      type: String,
      default: 'select',
    },
    renderInputText: {
      type: Function,
    },
    shortcuts: {
      type: Array,
      validator(value) {
        return (
          Array.isArray(value) &&
          value.every(
            v => isObject(v) && typeof v.text === 'string' && typeof v.onClick === 'function'
          )
        )
      },
      default() {
        return []
      },
    },
    hideDoubleArrows: {
      type: Boolean,
      default: true,
    },
  },
  i18n: {
    messages: {
      uk: {
        select: 'Вибрати',
      },
      ru: {
        select: 'Выбрать',
      },
      en: {
        select: 'Select',
      },
    }
  },
  data() {
    return {
      // cache the innervalue, wait to confirm
      currentValue: null,
      userInput: null,
      defaultOpen: false,
      mouseInInput: false,
    }
  },
  computed: {
    popupVisible() {
      return !this.disabled && (typeof this.open === 'boolean' ? this.open : this.defaultOpen)
    },
    innerRangeSeparator() {
      return this.rangeSeparator || (this.multiple ? ',' : ' - ')
    },
    innerFormat() {
      const map = {
        date: 'DD.MM.YYYY',
        datetime: 'DD.MM.YYYY HH:mm:ss',
        year: 'YYYY',
        month: 'MM.YYYY',
        time: 'HH:mm:ss',
        week: 'w',
      }
      return this.format || map[this.type] || map.date
    },
    innerValue() {
      let { value } = this
      if (this.validMultipleType) {
        value = Array.isArray(value) ? value : []
        return value.map(this.value2date)
      }
      if (this.range) {
        value = Array.isArray(value) ? value.slice(0, 2) : [null, null]
        return value.map(this.value2date)
      }
      return this.value2date(value)
    },
    labelText() {
      if (this.userInput !== null) {
        return this.userInput
      }
      if (typeof this.renderInputText === 'function') {
        return this.renderInputText(this.innerValue)
      }
      if (!this.isValidValue(this.innerValue)) {
        return ''
      }
      if (Array.isArray(this.innerValue)) {
        return this.innerValue.map(v => this.formatDate(v)).join(this.innerRangeSeparator)
      }
      return this.formatDate(this.innerValue)
    },
    showClearIcon() {
      return !this.disabled && this.clearable && this.labelText && this.mouseInInput
    },
    locale() {
      if (isObject(this.$i18n.locale)) {
        return mergeDeep(getLocale(), this.$i18n.locale)
      }
      return getLocale(this.$i18n.locale)
    },
    validMultipleType() {
      const types = ['date', 'month', 'year']
      return this.multiple && !this.range && types.indexOf(this.type) !== -1
    },
  },
  watch: {
    innerValue: {
      immediate: true,
      handler(val) {
        this.currentValue = val
      },
    },
    popupVisible: {
      handler(val) {
        if (val) {
          this.currentValue = this.innerValue
        }
      },
    },
  },
  created() {
    /*if (typeof this.format === 'object') {
      console.warn(
        '[vue2-datepicker]: The prop `format` don\'t support Object any more. You can use the new prop `formatter` to replace it'
      )
    }*/
  },
  methods: {
    handleMouseEnter() {
      this.mouseInInput = true
    },
    handleMouseLeave() {
      this.mouseInInput = false
    },
    handleClickOutSide(evt) {
      const { target } = evt
      if (!this.$el.contains(target)) {
        this.closePopup()
      }
    },
    getFormatter(key) {
      return (
        (isObject(this.formatter) && this.formatter[key]) ||
        (isObject(this.format) && this.format[key])
      )
    },
    getWeek(date, options) {
      if (typeof this.getFormatter('getWeek') === 'function') {
        return this.getFormatter('getWeek')(date, options)
      }
      return getWeek(date, options)
    },
    parseDate(value, fmt) {
      fmt = fmt || this.innerFormat
      if (typeof this.getFormatter('parse') === 'function') {
        return this.getFormatter('parse')(value, fmt)
      }
      const backupDate = new Date()
      return parse(value, fmt, { locale: this.locale.formatLocale, backupDate })
    },
    formatDate(date, fmt) {
      fmt = fmt || this.innerFormat
      if (typeof this.getFormatter('stringify') === 'function') {
        return this.getFormatter('stringify')(date, fmt)
      }
      return format(date, fmt, getLocale(this.$i18n.locale))
    },
    // transform the outer value to inner date
    value2date(value) {
      switch (this.valueType) {
      case 'date':
        return value instanceof Date ? new Date(value.getTime()) : new Date(NaN)
      case 'timestamp':
        return typeof value === 'number' ? new Date(value) : new Date(NaN)
      case 'format':
        return typeof value === 'string' ? this.parseDate(value) : new Date(NaN)
      default:
        return typeof value === 'string' ? this.parseDate(value, this.valueType) : new Date(NaN)
      }
    },
    // transform the inner date to outer value
    date2value(date) {
      if (!isValidDate(date)) { return null }
      switch (this.valueType) {
      case 'date':
        return date
      case 'timestamp':
        return date.getTime()
      case 'format':
        return this.formatDate(date)
      default:
        return this.formatDate(date, this.valueType)
      }
    },
    emitValue(date, type, close = true) {
      if (this.disabled) { return }
      // fix IE11/10 trigger input event when input is focused. (placeholder !== '')
      this.userInput = null
      const value = Array.isArray(date) ? date.map(this.date2value) : this.date2value(date)
      this.$emit('input', value)
      this.$emit('change', value, type)
      if (close) {
        this.closePopup()
      }
      return value
    },
    isValidValue(value) {
      if (this.validMultipleType) {
        return isValidDates(value)
      }
      if (this.range) {
        return isValidRangeDate(value)
      }
      return isValidDate(value)
    },
    isValidValueAndNotDisabled(value) {
      if (!this.isValidValue(value)) {
        return false
      }
      const disabledDate =
        typeof this.disabledDate === 'function' ? this.disabledDate : () => false
      const disabledTime =
        typeof this.disabledTime === 'function' ? this.disabledTime : () => false
      if (!Array.isArray(value)) {
        value = [value]
      }
      return value.every(v => !disabledDate(v) && !disabledTime(v))
    },
    handleMultipleDates(date, dates) {
      if (this.validMultipleType && dates) {
        const nextDates = dates.filter(v => v.getTime() !== date.getTime())
        if (nextDates.length === dates.length) {
          nextDates.push(date)
        }
        return nextDates
      }
      return date
    },
    handleSelectDate(val, type, dates) {
      if (this.disabled) { return }
      val = this.handleMultipleDates(val, dates)
      if (this.confirm) {
        this.currentValue = val
      } else {
        this.emitValue(
          val,
          type,
          // this.type === 'datetime', click the time should close popup
          !this.validMultipleType && (type === this.type || type === 'time')
        )
      }
    },
    clear() {
      if (this.disabled) { return }
      this.emitValue(this.range ? [null, null] : null)
      this.$emit('clear')
    },
    handleClear(evt) {
      if (this.disabled) { return }
      evt.stopPropagation()
      this.clear()
    },
    handleConfirmDate() {
      const value = this.emitValue(this.currentValue)
      this.$emit('confirm', value)
    },
    handleSelectShortcut(evt) {
      if (this.disabled) { return }
      const index = evt.currentTarget.getAttribute('data-index')
      const item = this.shortcuts[parseInt(index, 10)]
      if (isObject(item) && typeof item.onClick === 'function') {
        const date = item.onClick(this)
        if (date) {
          this.emitValue(date)
        }
      }
    },
    openPopup(evt) {
      if (this.popupVisible || this.disabled) { return }
      this.defaultOpen = true
      this.$emit('open', evt)
      this.$emit('update:open', true)
    },
    closePopup() {
      if (!this.popupVisible) { return }
      this.defaultOpen = false
      this.$emit('close')
      this.$emit('update:open', false)
    },
    blur() {
      // when use slot input
      if (this.$refs.input) {
        this.$refs.input.blur()
      }
    },
    focus() {
      if (this.$refs.input) {
        this.$refs.input.focus()
      }
    },
    handleInputChange() {
      if (!this.editable || this.userInput === null) { return }
      const text = this.userInput.trim()
      this.userInput = null
      if (text === '') {
        this.clear()
        return
      }
      let date
      if (this.validMultipleType) {
        date = text.split(this.innerRangeSeparator).map(v => this.parseDate(v.trim()))
      } else if (this.range) {
        let arr = text.split(this.innerRangeSeparator)
        if (arr.length !== 2) {
          // Maybe the separator during the day is the same as the separator for the date
          // eg: 2019-10-09-2020-01-02
          arr = text.split(this.innerRangeSeparator.trim())
        }
        date = arr.map(v => this.parseDate(v.trim()))
      } else {
        date = this.parseDate(text)
      }
      if (this.isValidValueAndNotDisabled(date)) {
        this.emitValue(date)
        this.blur()
      } else {
        this.$emit('input-error', text)
      }
    },
    handleInputInput(evt) {
      // slot input v-model
      this.userInput = typeof evt === 'string' ? evt : evt.target.value
    },
    handleInputKeydown(evt) {
      const { keyCode } = evt
      // Tab 9 or Enter 13
      if (keyCode === 9) {
        this.closePopup()
      } else if (keyCode === 13) {
        this.handleInputChange()
      }
    },
    handleInputBlur(evt) {
      // tab close
      this.$emit('blur', evt)
    },
    handleInputFocus(evt) {
      this.openPopup(evt)
      this.$emit('focus', evt)
    },
    hasSlot(name) {
      return !!(this.$slots[name] || this.$scopedSlots[name])
    },
    renderSlot(name, fallback, props) {
      const slotFn = this.$scopedSlots[name]
      if (slotFn) {
        return slotFn(props) || fallback
      }
      return this.$slots[name] || fallback
    },
    renderInput() {
      const { prefixClass } = this

      const calendarIcon = <sIcon type={this.type === 'time' ? 'clock' : 'calendar'} />

      return (
        <sInput
          value={this.labelText}
          disabled={this.disabled}
          readonly={!this.editable}
          placeholder={this.placeholder}
          onMouseenter={this.handleMouseEnter}
          onMouseleave={this.handleMouseLeave}
          onKeydown={this.handleInputKeydown}
          onFocus={this.handleInputFocus}
          onBlur={this.handleInputBlur}
          onClick={this.openPopup}
          onInput={this.handleInputInput}
          onChange={this.handleInputChange}
          ref="inputWrapper"
        >
          <template slot="suffix">
            {this.showClearIcon ? (
              <i class={`${prefixClass}-icon-clear`} onClick={this.handleClear}>
                {this.renderSlot('icon-clear', <sIcon type="cancel" />)}
              </i>
            ) : (
              <i class={`${prefixClass}-icon-calendar`} onClick={this.openPopup}>
                {this.renderSlot('icon-calendar', calendarIcon)}
              </i>
            )}
          </template>
        </sInput>
      )
    },
    renderContent() {
      const map = this.range ? componentRangeMap : componentMap
      const Component = map[this.type] || map.default
      const props = {
        ...pick(this.$props, Object.keys(Component.props)),
        value: this.currentValue,
      }
      const on = {
        ...pick(this.$listeners, Component.emits || []),
        select: this.handleSelectDate,
      }
      const content = <Component {...{ props, on, ref: 'picker' }} />
      return (
        <div class={`${this.prefixClass}-datepicker-body`}>
          {this.renderSlot('content', content, {
            value: this.currentValue,
            emit: this.handleSelectDate,
          })}
        </div>
      )
    },
    renderSidebar() {
      const { prefixClass } = this
      return (
        <div class={`${prefixClass}-datepicker-sidebar`}>
          {this.renderSlot('sidebar', null, {
            value: this.currentValue,
            emit: this.handleSelectDate,
          })}
          {this.shortcuts.map((v, i) => (
            <sButton
              key={i}
              data-index={i}
              type="text"
              disabled={this.disabled}
              small
              class={`${prefixClass}-calendar-btn-shortcut`}
              onClick={this.handleSelectShortcut}
            >
              {v.text}
            </sButton>
          ))}
        </div>
      )
    },
    renderHeader() {
      return (
        <div class={`${this.prefixClass}-datepicker-header`}>
          {this.renderSlot('header', null, {
            value: this.currentValue,
            emit: this.handleSelectDate,
          })}
        </div>
      )
    },
    renderFooter() {
      const { prefixClass } = this
      return (
        <div class={`${prefixClass}-datepicker-footer`}>
          {this.renderSlot('footer', null, {
            value: this.currentValue,
            emit: this.handleSelectDate,
          })}
          {this.confirm ? (
            <sButton
              small
              type="text"
              class={`${prefixClass}-datepicker-btn-confirm`}
              onClick={this.handleConfirmDate}
            >
              {this.$t(this.confirmText)}
            </sButton>
          ) : null}
        </div>
      )
    },
  },
  render() {
    const { prefixClass, inline, disabled } = this
    const sidedar = this.hasSlot('sidebar') || this.shortcuts.length ? this.renderSidebar() : null
    const content = (
      <div class={`${prefixClass}-datepicker-content`}>
        {this.hasSlot('header') ? this.renderHeader() : null}
        {this.renderContent()}
        {this.hasSlot('footer') || this.confirm ? this.renderFooter() : null}
      </div>
    )

    return (
      <div
        class={{
          [`${prefixClass}-datepicker`]: true,
          [`${prefixClass}-datepicker-range`]: this.range,
          [`${prefixClass}-datepicker-type-${this.type}`]: this.type,
          [`${prefixClass}-datepicker-inline`]: inline,
          [`${prefixClass}-with-sidebar`]: this.hasSlot('sidebar') || this.shortcuts.length,
          disabled,
        }}
      >
        {!inline ? this.renderInput() : null}
        {!inline ? (
          <DatePopup
            ref="popup"
            class={`${this.popupClass ? this.popupClass : ''}${this.shortcuts.length || this.hasSlot('sidebar') ? ' with-sidebar' : ''}`}
            style={this.popupStyle}
            visible={this.popupVisible}
            appendToBody={this.appendToBody}
            onClickoutside={this.handleClickOutSide}
          >
            {sidedar}
            {content}
          </DatePopup>
        ) : (
          <div class={`${prefixClass}-datepicker-main`}>
            {sidedar}
            {content}
          </div>
        )}
      </div>
    )
  },
}